/**
 * Device information detection utilities
 * Captures browser, OS, and device type for passkey metadata
 */

export interface DeviceInfo {
    browser: string
    browserVersion: string
    os: string
    osVersion: string
    deviceType: 'desktop' | 'mobile' | 'tablet'
    isSynced: boolean
    syncProvider?: string
    platform: string
    userAgent: string
    screenResolution: string
    timezone: string
    language: string
    authenticatorAttachment?: 'platform' | 'cross-platform'
}

/**
 * Detect browser and version from user agent
 */
function detectBrowser(ua: string): { name: string; version: string } {
    // Chrome (must check before Safari as Chrome includes Safari string)
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
        const match = ua.match(/Chrome\/([\d.]+)/)
        return {name: 'Chrome', version: match?.[1]?.split('.')[0] || ''}
    }

    // Edge
    if (ua.includes('Edg')) {
        const match = ua.match(/Edg\/([\d.]+)/)
        return {name: 'Edge', version: match?.[1]?.split('.')[0] || ''}
    }

    // Firefox
    if (ua.includes('Firefox')) {
        const match = ua.match(/Firefox\/([\d.]+)/)
        return {name: 'Firefox', version: match?.[1]?.split('.')[0] || ''}
    }

    // Safari (must check after Chrome)
    if (ua.includes('Safari') && !ua.includes('Chrome')) {
        const match = ua.match(/Version\/([\d.]+)/)
        return {name: 'Safari', version: match?.[1]?.split('.')[0] || ''}
    }

    // Opera
    if (ua.includes('OPR') || ua.includes('Opera')) {
        const match = ua.match(/(?:OPR|Opera)\/([\d.]+)/)
        return {name: 'Opera', version: match?.[1]?.split('.')[0] || ''}
    }

    return {name: 'Unknown Browser', version: ''}
}

/**
 * Detect operating system and version
 */
function detectOS(ua: string, platform: string): { name: string; version: string } {
    // iOS
    if (/iPad|iPhone|iPod/.test(ua)) {
        const match = ua.match(/OS ([\d_]+)/)
        const version = match?.[1]?.replace(/_/g, '.').split('.').slice(0, 2).join('.') || ''

        if (ua.includes('iPad')) {
            return {name: 'iPadOS', version}
        }
        return {name: 'iOS', version}
    }

    // macOS
    if (platform.startsWith('Mac')) {
        const match = ua.match(/Mac OS X ([\d_]+)/)
        const version = match?.[1]?.replace(/_/g, '.').split('.').slice(0, 2).join('.') || ''
        return {name: 'macOS', version}
    }

    // Windows
    if (platform.startsWith('Win')) {
        // Windows 11
        if (ua.includes('Windows NT 10.0') && ua.includes('Win64')) {
            // Check for Windows 11 (user agent still shows NT 10.0)
            // This is a heuristic - Windows 11 detection is not reliable via UA
            return {name: 'Windows', version: '11'}
        }
        // Windows 10
        if (ua.includes('Windows NT 10.0')) {
            return {name: 'Windows', version: '10'}
        }
        return {name: 'Windows', version: ''}
    }

    // Android
    if (ua.includes('Android')) {
        const match = ua.match(/Android ([\d.]+)/)
        const version = match?.[1]?.split('.')[0] || ''
        return {name: 'Android', version}
    }

    // Linux
    if (platform.startsWith('Linux')) {
        return {name: 'Linux', version: ''}
    }

    return {name: 'Unknown OS', version: ''}
}

/**
 * Detect device type based on user agent and screen size
 */
function detectDeviceType(ua: string): 'desktop' | 'mobile' | 'tablet' {
    // Check for tablet indicators
    if (/iPad/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) {
        return 'tablet'
    }

    // Check for mobile indicators
    if (/iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|Opera Mini|IEMobile/.test(ua)) {
        return 'mobile'
    }

    // Check screen size as additional heuristic
    if (window.innerWidth <= 768 && 'ontouchstart' in window) {
        return 'mobile'
    }

    if (window.innerWidth <= 1024 && 'ontouchstart' in window) {
        return 'tablet'
    }

    return 'desktop'
}

/**
 * Detect sync provider based on OS and browser
 */
function detectSyncProvider(os: string, browser: string, authenticatorAttachment?: string): {
    isSynced: boolean;
    provider?: string
} {
    if (authenticatorAttachment === 'cross-platform') {
        // Security keys are not synced
        return {isSynced: false, provider: 'Security Key'}
    }

    // Apple ecosystem
    if (os.includes('iOS') || os.includes('iPadOS') || os.includes('macOS')) {
        return {isSynced: true, provider: 'iCloud Keychain'}
    }

    // Google ecosystem
    if (browser === 'Chrome' && (os.includes('Android') || os.includes('Windows') || os.includes('macOS'))) {
        return {isSynced: true, provider: 'Google Password Manager'}
    }

    // Microsoft ecosystem
    if (os.includes('Windows') && (browser === 'Edge' || browser === 'Chrome')) {
        return {isSynced: true, provider: 'Windows Hello'}
    }

    // Samsung devices
    if (os.includes('Android') && navigator.userAgent.includes('Samsung')) {
        return {isSynced: true, provider: 'Samsung Pass'}
    }

    return {isSynced: false}
}

/**
 * Get comprehensive device information
 */
export function getDeviceInfo(authenticatorAttachment?: 'platform' | 'cross-platform'): DeviceInfo {
    const ua = navigator.userAgent
    const platform = navigator.platform || ''

    const browser = detectBrowser(ua)
    const os = detectOS(ua, platform)
    const deviceType = detectDeviceType(ua)
    const syncInfo = detectSyncProvider(os.name, browser.name, authenticatorAttachment)

    // Get screen resolution
    const screenResolution = `${window.screen.width}x${window.screen.height}`

    // Get timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    // Get language
    const language = navigator.language || 'en-US'

    return {
        browser: browser.name,
        browserVersion: browser.version,
        os: os.name,
        osVersion: os.version,
        deviceType,
        isSynced: syncInfo.isSynced,
        syncProvider: syncInfo.provider,
        platform,
        userAgent: ua,
        screenResolution,
        timezone,
        language,
        authenticatorAttachment
    }
}

/**
 * Format device info for display
 */
export function formatDeviceInfo(info: DeviceInfo): string {
    let result = `${info.browser}`
    if (info.browserVersion) {
        result += ` ${info.browserVersion}`
    }

    result += ` on ${info.os}`
    if (info.osVersion) {
        result += ` ${info.osVersion}`
    }

    return result
}

/**
 * Get a friendly device name suggestion based on device info
 */
export function suggestDeviceName(info: DeviceInfo): string {
    const prefix = info.deviceType === 'mobile' ? 'Phone' :
        info.deviceType === 'tablet' ? 'Tablet' :
            'Computer'

    return `${prefix} - ${info.browser} on ${info.os}`
}
